import { useState } from "react";
import {
  Box,
  Button,
  Autocomplete,
  FormControl,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Add, RestartAlt } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { SelectOption } from "../MainBody/Form";
import { LabelForAuto } from "./StyledComponents";
import { forwardRef, useEffect, useRef } from "react";
import { Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const BootstrapInputForSelectField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    fontSize: "16px",
    padding: "6px",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "#E1E2EB",
      border:
        theme.palette.mode === "dark"
          ? "1px solid #4B5563"
          : "1px solid #D1D5DB",
    },
    fontFamily: "San Francisco Text",
    "&.Mui-focused fieldset": {
      boxShadow: `rgb(25 118 210 / 25%) 0 0 0 0.2rem`,
      border: `1px solid #1976d2`,
    },
  },
  "& .Mui-disabled": {
    backgroundColor: theme.palette.mode === "dark" ? "#21262C" : "#e5e7e9",
  },
}));

interface Option {
  id: string;
  label: string;
  fuel?: string;
}

const AutoCompleteInput = forwardRef(
  (
    props: {
      files?: any;
      label: string;
      listOption: SelectOption[];
      onChange?: Function;
      value: SelectOption | undefined;
      disabled?: boolean;
      placeholder?: string;
      open?: boolean;
      onClose?: Function;
      onOpen?: Function;
      setFiles?: Function;
      setDataFromImage?: Function;
      disabledReset?: boolean;
      hidden?: boolean;
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const theme = useTheme();
    const [inputText, setInputText] = useState<string>("");

    useEffect(() => {
      if (props.open && inputRef.current) {
        inputRef.current.focus();
      }
    }, [props.open]);

    const getOptionLabel = (option: Option) => {
      if (!option) return "";

      if (option.id !== "" && option.fuel) {
        return `${option.label} (${option.fuel})`;
      }

      if (option.label === "LRTN" && Number(option.id) === 2) {
        return "Lắp ráp trong nước";
      }

      if (option.label === "NK" && Number(option.id) === 1) {
        return "Nhập khẩu";
      }

      return option.label ?? "";
    };

    const handleReset = () => {
      props?.onChange && props.onChange({ id: "", label: "" });
    };

    return (
      <FormControl variant="standard" sx={{ minWidth: "100%" }}>
        <LabelForAuto
          style={{ color: theme.palette.mode === "dark" ? "white" : "black" }}
        >
          {props.label}
        </LabelForAuto>
        <Tooltip title={props.disabled ? "" : props.value?.label} followCursor>
          {props.value?.id !== "other" ||
          props.label === "Năm sản xuất" ||
          props.label === "Loại thùng" ? (
            <Autocomplete
              value={
                props.listOption.find((o) => o.id === props.value?.id) ?? null
              }
              inputValue={inputText}
              onInputChange={(_, newInputValue) => {
                setInputText(newInputValue);
              }}
              disablePortal
              options={props.listOption}
              getOptionLabel={getOptionLabel}
              sx={{ width: "100%" }}
              onChange={(_, newValue) => {
                _.stopPropagation();
                if (props.onChange) {
                  const currentValue = newValue
                    ? newValue
                    : { id: "", label: "" };
                  props.onChange(currentValue);
                  setInputText("");
                  if (props.files) {
                    props.setFiles && props.setFiles([]);
                  }
                }
              }}
              noOptionsText={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">Không có dữ liệu</Typography>
                  {props.label !== "Năm sản xuất" &&
                    props.label !== "Loại thùng" &&
                    !props.hidden && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!inputText}
                        endIcon={<Add />}
                        sx={{ textTransform: "unset", cursor: "pointer" }}
                        onClick={() =>
                          props.onChange &&
                          props.onChange({ id: "other", label: inputText })
                        }
                      >
                        <Typography variant="body2">Thêm mới</Typography>
                      </Button>
                    )}
                </Box>
              }
              renderOption={(props, option) => {
                const { ...rest } = props;

                return (
                  <li {...rest} key={option.id}>
                    {getOptionLabel(option)}
                  </li>
                );
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={props.disabled}
              renderInput={(params) => (
                <BootstrapInputForSelectField
                  {...params}
                  placeholder={props.placeholder}
                  size="small"
                  inputRef={(el) => {
                    inputRef.current = el;
                    if (ref) {
                      if (typeof ref === "function") {
                        ref(el);
                      } else {
                        ref.current = el;
                      }
                    }
                  }}
                />
              )}
              open={props.open}
              onClose={() => {
                setInputText("");
                if (props.onClose) props.onClose();
              }}
              onOpen={() => {
                if (props.onOpen) {
                  props.onOpen();
                }
              }}
            />
          ) : (
            <BootstrapInputForSelectField
              value={props?.value?.label}
              placeholder={props?.placeholder}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {!props.disabledReset && (
                      <IconButton
                        onClick={handleReset}
                        disabled={props.disabledReset}
                      >
                        <RestartAlt />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              onChange={(event) => {
                if (props?.onChange) {
                  props.onChange({ id: "other", label: event.target.value });
                }
              }}
              disabled={props.disabled}
            />
          )}
        </Tooltip>
      </FormControl>
    );
  },
);

export default AutoCompleteInput;
